import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { parsePiiMarkers } from '../../../utils/parsePiiMarkers';
import { RedactionText } from '../../redaction/RedactionText';

interface PageEventInfo {
  headline: string;
  eventType: string;
  eventDate?: string;
  significance?: string;
}

interface DocumentSummaryCardProps {
  document: { documentId: string; filename: string; pageCount?: number; processingStatus?: string };
  docType?: { documentId: string; type: string; confidence: number; summary?: string; citations?: any[] };
  dateRange: { earliest: string | null; latest: string | null; topThemes: string[] };
  viewedPages: number;
  chronologyEvents?: any[];
  onClick: () => void;
  onOpenAtPage?: (pageNumber: number) => void;
}

const typeAccentColour: Record<string, string> = {
  clinic_letter: 'bg-cyan-500',
  correspondence: 'bg-cyan-500',
  discharge_summary: 'bg-green-500',
  patient_record: 'bg-green-500',
  nursing_notes: 'bg-purple-500',
  investigation_report: 'bg-amber-500',
  lab_results: 'bg-amber-500',
  imaging_report: 'bg-amber-500',
  medical_report: 'bg-teal-500',
};

function getAccentColour(type?: string): string {
  if (!type) return 'bg-medical-400';
  return typeAccentColour[type] ?? 'bg-medical-400';
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function formatShortDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTypeLabel(type: string): string {
  return type.replace(/_/g, ' ');
}

function formatEventType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const EVENT_TYPE_COLOURS: Record<string, string> = {
  ADMISSION: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  DISCHARGE: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  DIAGNOSIS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  MAJOR_PROCEDURE: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  MEDICATION: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  LAB_RESULT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  INVESTIGATION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

const SIGNIFICANCE_COLOURS: Record<string, string> = {
  MAJOR: 'bg-primary-500',
  SUPPORTING: 'bg-amber-500',
  ROUTINE: 'bg-medical-300 dark:bg-medical-600',
};

const DEFAULT_TYPE_COLOUR = 'bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-300';

interface PageGroup {
  startPage: number;
  endPage: number;
  events: PageEventInfo[];
}

/**
 * Build page groups from chronology events that cite this document.
 * Groups consecutive pages with no events together, but keeps pages with events
 * as individual entries so their headlines are visible.
 */
function buildPageGroups(
  pageCount: number,
  pageEventsMap: Map<number, PageEventInfo[]>,
): PageGroup[] {
  if (pageCount === 0) return [];

  const groups: PageGroup[] = [];
  let emptyRangeStart: number | null = null;

  for (let p = 1; p <= pageCount; p++) {
    const events = pageEventsMap.get(p);
    if (!events || events.length === 0) {
      if (emptyRangeStart === null) emptyRangeStart = p;
    } else {
      // Flush any pending empty range
      if (emptyRangeStart !== null) {
        groups.push({ startPage: emptyRangeStart, endPage: p - 1, events: [] });
        emptyRangeStart = null;
      }
      groups.push({ startPage: p, endPage: p, events });
    }
  }
  // Flush trailing empty range
  if (emptyRangeStart !== null) {
    groups.push({ startPage: emptyRangeStart, endPage: pageCount, events: [] });
  }

  return groups;
}

export const DocumentSummaryCard: React.FC<DocumentSummaryCardProps> = ({
  document,
  docType,
  dateRange,
  viewedPages,
  chronologyEvents,
  onClick,
  onOpenAtPage,
}) => {
  const pageCount = document.pageCount ?? 0;
  const progressFraction = pageCount > 0 ? Math.min(viewedPages / pageCount, 1) : 0;
  const [expanded, setExpanded] = useState(false);

  // Build per-page event index from chronology citations
  const pageEventsMap = useMemo(() => {
    const map = new Map<number, PageEventInfo[]>();
    if (!chronologyEvents) return map;

    for (const event of chronologyEvents) {
      const citations = event.citations as Array<{ documentId: string; pageNumber: number }> | undefined;
      if (!citations) continue;
      for (const citation of citations) {
        if (citation.documentId !== document.documentId) continue;
        const page = citation.pageNumber;
        if (!map.has(page)) map.set(page, []);
        // Avoid duplicate headlines on the same page
        const existing = map.get(page)!;
        if (existing.some(e => e.headline === (event.headline || event.description))) continue;
        existing.push({
          headline: event.headline || event.description,
          eventType: event.eventType,
          eventDate: event.eventDate,
          significance: event.significance,
        });
      }
    }
    return map;
  }, [chronologyEvents, document.documentId]);

  const pageGroups = useMemo(() => {
    if (!expanded) return [];
    return buildPageGroups(pageCount, pageEventsMap);
  }, [expanded, pageCount, pageEventsMap]);

  const hasAnyEvents = pageEventsMap.size > 0;

  return (
    <div
      className={clsx(
        'w-full text-left relative flex flex-col rounded-lg border border-medical-200 dark:border-medical-700',
        'hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm',
        'transition-all duration-150 bg-white dark:bg-medical-800',
      )}
    >
      <div className="flex">
        {/* Left accent stripe */}
        <div
          className={clsx(
            'w-1 shrink-0 rounded-l-lg',
            getAccentColour(docType?.type),
          )}
        />

        {/* Card content */}
        <div className="flex-1 min-w-0 px-3 py-2.5">
          {/* Header: filename + type badge */}
          <div className="flex items-start gap-2">
            {hasAnyEvents && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(v => !v);
                }}
                className="p-0.5 -ml-1 shrink-0 text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 transition-colors"
                title={expanded ? 'Collapse page breakdown' : 'Expand page breakdown'}
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            <button
              onClick={onClick}
              className={clsx(
                'text-sm font-medium text-medical-800 dark:text-medical-200 truncate flex-1 min-w-0 text-left hover:text-primary-600 dark:hover:text-primary-400 transition-colors',
                !hasAnyEvents && 'ml-0',
              )}
            >
              {document.filename}
            </button>
            {docType && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 rounded uppercase">
                {formatTypeLabel(docType.type)}
              </span>
            )}
          </div>

          {/* Page count */}
          {pageCount > 0 && (
            <p className={clsx('text-[10px] text-medical-400 mt-0.5', hasAnyEvents && 'ml-5')}>
              {pageCount} page{pageCount !== 1 ? 's' : ''}
            </p>
          )}

          {/* Summary */}
          {docType?.summary && (
            <p className={clsx('mt-1.5 text-xs text-medical-500 dark:text-medical-400 leading-relaxed line-clamp-3', hasAnyEvents && 'ml-5')}>
              <RedactionText text={parsePiiMarkers(docType.summary).cleanText} />
            </p>
          )}

          {/* Footer row */}
          <div className={clsx('flex items-center gap-2 mt-2 flex-wrap', hasAnyEvents && 'ml-5')}>
            {/* Date range */}
            {dateRange.earliest && (
              <span className="text-[10px] text-medical-400">
                {formatDate(dateRange.earliest)}
                {dateRange.latest && dateRange.latest !== dateRange.earliest && (
                  <> &mdash; {formatDate(dateRange.latest)}</>
                )}
              </span>
            )}

            {/* Theme pills */}
            {dateRange.topThemes.map((theme) => (
              <span
                key={theme}
                className="px-1.5 py-0.5 text-[9px] rounded-full bg-medical-100 text-medical-600 dark:bg-medical-700 dark:text-medical-300"
              >
                {formatTypeLabel(theme)}
              </span>
            ))}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Reading progress mini-bar */}
            {pageCount > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-10 h-1 rounded-full bg-medical-100 dark:bg-medical-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-green-500 transition-all duration-300"
                    style={{ width: `${progressFraction * 100}%` }}
                  />
                </div>
                <span className="text-[9px] text-medical-400 tabular-nums">
                  {Math.min(viewedPages, pageCount)}/{pageCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded: page-by-page breakdown with event headlines */}
      {expanded && (
        <div className="border-t border-medical-100 dark:border-medical-700 py-1.5">
          <div className="max-h-[360px] overflow-y-auto">
            {pageGroups.map((group) => (
              <div key={group.startPage}>
                {group.events.length === 0 ? (
                  /* Empty page range — compact single line */
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenAtPage?.(group.startPage);
                    }}
                    className="w-full text-left flex items-center gap-2 px-4 py-1 hover:bg-medical-50 dark:hover:bg-medical-700/30 transition-colors"
                  >
                    <span className="text-[10px] tabular-nums text-medical-400 min-w-[44px] shrink-0">
                      {group.startPage === group.endPage
                        ? `p.${group.startPage}`
                        : `p.${group.startPage}–${group.endPage}`}
                    </span>
                    <span className="text-[9px] text-medical-300 dark:text-medical-600 italic">
                      No linked events
                    </span>
                  </button>
                ) : (
                  /* Page with events — show headlines */
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenAtPage?.(group.startPage);
                    }}
                    className="w-full text-left px-4 py-1.5 hover:bg-medical-50 dark:hover:bg-medical-700/30 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[10px] tabular-nums font-medium text-medical-500 dark:text-medical-400 min-w-[32px] shrink-0 pt-0.5">
                        p.{group.startPage}
                      </span>
                      <div className="flex-1 min-w-0 space-y-1">
                        {group.events.slice(0, 4).map((event, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            {/* Significance dot */}
                            <span className={clsx(
                              'mt-1 h-1.5 w-1.5 rounded-full shrink-0',
                              SIGNIFICANCE_COLOURS[event.significance ?? ''] ?? SIGNIFICANCE_COLOURS.ROUTINE,
                            )} />
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] text-medical-700 dark:text-medical-200 leading-tight line-clamp-1">
                                {event.headline}
                              </p>
                              <div className="flex items-center gap-1 mt-0.5">
                                {event.eventDate && (
                                  <span className="text-[8px] text-medical-400 tabular-nums">
                                    {formatShortDate(event.eventDate)}
                                  </span>
                                )}
                                <span className={`px-1 py-0.5 text-[7px] font-medium rounded ${
                                  EVENT_TYPE_COLOURS[event.eventType] || DEFAULT_TYPE_COLOUR
                                }`}>
                                  {formatEventType(event.eventType)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {group.events.length > 4 && (
                          <p className="text-[9px] text-medical-400">
                            +{group.events.length - 4} more event{group.events.length - 4 !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
